import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Figma,
  Camera,
  Check,
  ClipboardCopy,
  Code2,
  Folder,
  KeyRound,
  Paintbrush,
  Layers,
  RefreshCw,
  Settings,
  CheckCircle2,
  ArrowRight,
  Search,
  Monitor,
  Smartphone,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getHostLabel } from "@/lib/chat/cli-guidance";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import { useOnboarding, type DesignerPlatform } from "@/hooks/use-onboarding";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";
import type { HelpStepComponentProps } from "../help-flows";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, description, accentClass }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; accentClass: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={cn("h-5 w-5", accentClass)} />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

const FIGMA_TOKEN_PLACEHOLDER = "<your-token>";

const getFigmaMcpConfigBlock = (token?: string) => {
  const tokenValue = JSON.stringify(token ?? FIGMA_TOKEN_PLACEHOLDER);

  return [
    '"figma": {',
    '  "type": "stdio",',
    '  "command": "npx",',
    '  "args": ["-y", "@anthropic/figma-mcp-server"],',
    '  "env": {',
    `    "FIGMA_PERSONAL_ACCESS_TOKEN": ${tokenValue}`,
    '  }',
    '}',
  ].join("\n");
};

interface DesignerPlatformOption {
  id: DesignerPlatform;
  label: string;
  subtitle: string;
  icon: ReactNode;
  accentClass: string;
  borderClass: string;
}

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-8 w-8" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const DESIGNER_PLATFORMS: DesignerPlatformOption[] = [
  {
    id: "web",
    label: "Web",
    subtitle: "React, Angular, Vue, Svelte, Next.js, Nuxt, and static HTML",
    icon: <Monitor className="h-8 w-8" />,
    accentClass: "bg-violet-500/15 text-violet-300",
    borderClass: "border-violet-500/30",
  },
  {
    id: "ios",
    label: "iOS",
    subtitle: "SwiftUI and UIKit with Xcode",
    icon: <AppleIcon />,
    accentClass: "bg-blue-500/15 text-blue-300",
    borderClass: "border-blue-500/30",
  },
  {
    id: "android",
    label: "Android",
    subtitle: "Jetpack Compose and XML layouts with Android Studio",
    icon: <Smartphone className="h-8 w-8" />,
    accentClass: "bg-emerald-500/15 text-emerald-300",
    borderClass: "border-emerald-500/30",
  },
];

export function DesignerPlatformPickerStep({ onAdvance }: Pick<HelpStepComponentProps, "onAdvance">) {
  const { selectedDesignerPlatform, setSelectedDesignerPlatform } = useOnboarding();
  const [chosen, setChosen] = useState<DesignerPlatform>(selectedDesignerPlatform ?? "web");
  const gridRef = useRef<HTMLDivElement>(null);
  useScrollSpotlight(gridRef);

  useEffect(() => {
    setSelectedDesignerPlatform(chosen);
  }, [chosen, setSelectedDesignerPlatform]);

  const handlePlatformClick = (platform: DesignerPlatform) => {
    if (platform === chosen) {
      onAdvance?.();
      return;
    }

    setChosen(platform);
  };

  return (
    <div className="space-y-5">
      <div ref={gridRef} className="grid gap-3 md:grid-cols-3">
        {DESIGNER_PLATFORMS.map((platform) => {
          const active = platform.id === chosen;

          return (
            <button
              key={platform.id}
              type="button"
              onClick={() => {
                handlePlatformClick(platform.id);
              }}
              className={cn(
                "group min-h-[200px] rounded-xl border bg-white/[0.03] p-5 text-left transition-all hover:border-white/[0.1] hover:bg-white/[0.05]",
                active ? `${platform.borderClass} bg-white/[0.05] ring-1 ring-white/[0.08]` : "border-white/[0.06]"
              )}
            >
              <div className="flex h-full flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div className={cn("inline-flex rounded-xl border border-white/[0.08] p-3", platform.accentClass)}>
                    {platform.icon}
                  </div>
                  {active ? (
                    <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-2.5 py-1 text-[11px] font-medium text-foreground/80">
                      Selected
                    </span>
                  ) : null}
                </div>
                <div className="mt-5 space-y-1.5">
                  <p className="text-base font-semibold text-foreground">{platform.label}</p>
                  <p className="text-sm leading-6 text-muted-foreground">{platform.subtitle}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-sm leading-6 text-muted-foreground">
        Pick the target platform Designer should optimize for. Click the selected card again to continue.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1 — What is SpecTree Designer?                                       */
/* -------------------------------------------------------------------------- */

export function WhatIsDesignerStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="What does SpecTree Designer do?">
        <p className="text-sm leading-7 text-muted-foreground">
          SpecTree Designer turns Figma designs into Build, Fix, or UX Audit workflows. You give it
          a Figma frame link, and it can generate a brand-new implementation, bring existing code
          back into visual alignment, or inspect the experience for UX issues while reusing the
          components and tokens already in your codebase.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          It supports web frameworks, iOS, and Android projects — including React, Angular, Vue,
          Svelte, Next.js, Nuxt, static HTML, and native/mobile workflows.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Three modes">
        <div className="grid gap-3 md:grid-cols-3">
          <InfoCard
            icon={Code2}
            title="Build mode"
            description="Creates a brand-new component or screen from the Figma design. Use it when you're implementing a fresh Web, iOS, or Android experience from scratch."
            accentClass="text-blue-400"
          />
          <InfoCard
            icon={Paintbrush}
            title="Fix mode"
            description="Takes existing code and adjusts it to match the Figma design. Use it when an existing Web, iOS, or Android implementation drifted from the source design."
            accentClass="text-blue-400"
          />
          <InfoCard
            icon={Search}
            title="UX Audit mode"
            description="Scans the current experience for accessibility issues, visual inconsistencies, and design-system compliance gaps, then produces a report you can act on."
            accentClass="text-emerald-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="How the workflows compare">
        <p className="text-sm leading-7 text-muted-foreground">
          Build mode and Fix mode both run the visual comparison loop: SpecTree Designer renders the
          implementation with Playwright, compares it pixel-by-pixel against the Figma reference
          using pixelmatch, and iterates on layout, colors, typography, and spacing until the diff
          drops below 1%.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          UX Audit mode follows a different path. Instead of regenerating code, SpecTree Designer
          scans the implemented experience for accessibility and design-system compliance issues,
          then produces an accessibility-focused report you can use to prioritize fixes.
        </p>
        <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-5 py-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {/* eslint-disable-next-line @typescript-eslint/no-deprecated */}
            <Figma className="h-4 w-4 text-purple-400" />
            <span>Figma frame</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Code2 className="h-4 w-4 text-blue-400" />
            <span>Build or fix code</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Camera className="h-4 w-4 text-blue-400" />
            <span>Screenshot</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RefreshCw className="h-4 w-4 text-amber-400" />
            <span>Compare &amp; iterate</span>
          </div>
        </div>
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-5 py-4">
          <div className="flex items-center gap-2 text-sm text-emerald-100/90">
            <Search className="h-4 w-4 text-emerald-400" />
            <span>UX Audit: scan implementation → accessibility &amp; design-system report</span>
          </div>
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

function DesignerPlatformToolSetup({ platform }: { platform: DesignerPlatform }) {
  switch (platform) {
    case "ios":
      return (
        <>
          <NumberedInstruction step={2} title="Install Xcode">
            <p className="text-sm leading-7 text-muted-foreground">
              Designer needs Xcode and the Apple command line tools to build and inspect iOS targets locally.
            </p>
            <p className="text-sm leading-7 text-muted-foreground">
              Install Xcode from the Mac App Store, open it once to finish setup, then install the command line tools:
            </p>
            <CopyableTerminal title="Terminal" copyText="xcode-select --install">
              <p><span className="text-foreground/45">$ </span><span className="text-foreground">xcode-select --install</span></p>
            </CopyableTerminal>
          </NumberedInstruction>

          <NumberedInstructionLast step={3} title="Set up iOS Simulator">
            <p className="text-sm leading-7 text-muted-foreground">
              Open <strong className="text-foreground/90">Xcode &gt; Window &gt; Devices and Simulators</strong> to confirm you have an iPhone or iPad simulator available.
            </p>
            <CopyableTerminal title="Verify simulators" copyText="xcrun simctl list devices">
              <p><span className="text-foreground/45">$ </span><span className="text-foreground">xcrun simctl list devices</span></p>
            </CopyableTerminal>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              You should see available simulator devices in the output. If the list is empty, add a runtime in Xcode and re-open the Simulators window.
            </p>
          </NumberedInstructionLast>
        </>
      );
    case "android":
      return (
        <>
          <NumberedInstruction step={2} title="Install Android Studio">
            <p className="text-sm leading-7 text-muted-foreground">
              Designer needs Android Studio plus the Android SDK to build and preview Android implementations.
            </p>
            <p className="text-sm leading-7 text-muted-foreground">
              Download Android Studio from <strong className="text-foreground/90">developer.android.com</strong>, then complete the first-run setup so the Android SDK, platform tools, and emulator packages are installed.
            </p>
          </NumberedInstruction>

          <NumberedInstructionLast step={3} title="Set up Android Emulator">
            <p className="text-sm leading-7 text-muted-foreground">
              Open <strong className="text-foreground/90">Android Studio &gt; Device Manager</strong>, create an AVD, and start the emulator you want Designer to target.
            </p>
            <CopyableTerminal title="Verify emulator connection" copyText="adb devices">
              <p><span className="text-foreground/45">$ </span><span className="text-foreground">adb devices</span></p>
            </CopyableTerminal>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              With the emulator running, the device should appear in the list. If it does not, reinstall the Android platform tools or restart the emulator.
            </p>
          </NumberedInstructionLast>
        </>
      );
    case "web":
    default:
      return (
        <>
          <NumberedInstruction step={2} title="Install Playwright">
            <p className="text-sm leading-7 text-muted-foreground">
              Playwright takes screenshots of your running app for pixel comparison. Install it as a dev dependency in your project:
            </p>
            <div className="space-y-3">
              <CopyableTerminal title="Install Playwright" copyText="pnpm add -D playwright">
                <p><span className="text-foreground/45">$ </span><span className="text-foreground">pnpm add -D playwright</span></p>
              </CopyableTerminal>
              <CopyableTerminal title="Install Chromium" copyText="npx playwright install chromium">
                <p><span className="text-foreground/45">$ </span><span className="text-foreground">npx playwright install chromium</span></p>
              </CopyableTerminal>
            </div>
          </NumberedInstruction>

          <NumberedInstructionLast step={3} title="Install pixelmatch">
            <p className="text-sm leading-7 text-muted-foreground">
              pixelmatch handles the pixel-by-pixel comparison between the Figma reference screenshot and your implementation screenshot:
            </p>
            <CopyableTerminal title="Terminal" copyText="pnpm add -D pixelmatch pngjs">
              <p><span className="text-foreground/45">$ </span><span className="text-foreground">pnpm add -D pixelmatch pngjs</span></p>
            </CopyableTerminal>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              Using npm? Replace <code className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs text-foreground/80">pnpm add -D</code> with <code className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs text-foreground/80">npm install -D</code>.
            </p>
          </NumberedInstructionLast>
        </>
      );
  }
}

/* -------------------------------------------------------------------------- */
/*  Step 2 — Set up prerequisites                                             */
/* -------------------------------------------------------------------------- */

export function DesignerPrerequisitesStep() {
  const { selectedPlatform, selectedDesignerPlatform } = useOnboarding();
  const platform = selectedPlatform ?? "mac";
  const designerPlatform = selectedDesignerPlatform ?? "web";
  const isMac = platform === "mac";
  const configPath = isMac
    ? "/Users/{your-username}/.copilot/mcp-config.json"
    : "C:\\Users\\{your-username}\\.copilot\\mcp-config.json";
  const openCommand = isMac
    ? "open ~/.copilot/mcp-config.json"
    : "notepad %USERPROFILE%\\.copilot\\mcp-config.json";
  const [figmaToken, setFigmaToken] = useState("");
  const [copiedConfig, setCopiedConfig] = useState(false);

  const host = useResolvedHost();
  const trimmedFigmaToken = figmaToken.trim();
  const hasValidFigmaToken = trimmedFigmaToken.length > 0;
  const figmaConfigBlock = getFigmaMcpConfigBlock(trimmedFigmaToken);
  const figmaMcpAddCommand = `claude mcp add --transport stdio --scope local figma --env FIGMA_PERSONAL_ACCESS_TOKEN=${trimmedFigmaToken || FIGMA_TOKEN_PLACEHOLDER} -- npx -y @anthropic/figma-mcp-server`;

  const copyConfig = async () => {
    await navigator.clipboard.writeText(figmaConfigBlock);
    setCopiedConfig(true);
    setTimeout(() => { setCopiedConfig(false); }, 2000);
  };

  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Add the Figma MCP server">
        <p className="text-sm leading-7 text-muted-foreground">
          Designer needs a connection to Figma to extract designs.{" "}
          {host === "copilot"
            ? "Add the Figma MCP server to the same config file you set up for SpecTree."
            : "Register the Figma MCP server the same way you registered SpecTree — with one claude mcp add command."}
        </p>

        {/* Platform-specific config path — Copilot edits a config file by hand */}
        {host === "copilot" ? (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.06]">
                <Folder className="h-4 w-4 text-muted-foreground/70" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground/60">
                  {isMac ? "macOS" : "Windows"} config path
                </p>
                <p className="mt-1.5 font-mono text-sm text-foreground/90 break-all">
                  {configPath}
                </p>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                  Replace <code className="rounded bg-white/[0.08] px-1 py-0.5 font-mono text-[10px]">{"{your-username}"}</code>{" "}
                  with your actual computer username.
                </p>
              </div>
            </div>

            {/* How to open it */}
            <div className="border-t border-white/[0.06] pt-4">
              <p className="text-xs font-semibold text-muted-foreground/80 mb-2">
                Quickest way to open it:
              </p>
              <CopyableTerminal title={isMac ? "Terminal" : "PowerShell"} copyText={openCommand}>
                <p>
                  <span className="text-foreground/45">{isMac ? "$ " : "> "}</span>
                  {openCommand}
                </p>
              </CopyableTerminal>
              <p className="mt-2 text-xs text-muted-foreground">
                {isMac
                  ? "This opens the file in your default text editor."
                  : "This opens the file in Notepad. If it doesn't exist yet, Notepad will ask to create it."}
              </p>
            </div>
          </div>
        </div>
        ) : null}

        {host === "copilot" ? (
        <p className="text-sm leading-7 text-muted-foreground">
          Add this entry inside the existing{" "}
          <code className="rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-xs text-foreground/80">{'"mcpServers"'}</code>{" "}
          object, next to the{" "}
          <code className="rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-xs text-foreground/80">{'"spectree"'}</code>{" "}
          entry:
        </p>
        ) : null}
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
          <label htmlFor="figma-token" className="text-sm font-medium text-foreground/90">
            Paste your Figma token
          </label>
          <div
            className={cn(
              "mt-3 flex items-center gap-3 rounded-lg border bg-white/[0.04] px-3.5 py-3 transition-all focus-within:border-purple-500/40 focus-within:ring-1 focus-within:ring-purple-500/30",
              hasValidFigmaToken ? "border-emerald-500/30 bg-white/[0.06]" : "border-white/[0.08]"
            )}
          >
            <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground/60" />
            <input
              id="figma-token"
              type="text"
              value={figmaToken}
              onChange={(event) => {
                setFigmaToken(event.target.value);
              }}
              placeholder="figd_..."
              className={cn(
                "w-full bg-transparent font-mono text-sm outline-none placeholder:text-muted-foreground/45",
                hasValidFigmaToken ? "text-emerald-400" : "text-foreground/80"
              )}
            />
            {hasValidFigmaToken ? <Check className="h-4 w-4 shrink-0 text-emerald-400" /> : null}
          </div>
        </div>
        {host === "copilot" ? (
          <CopyableTerminal
            title="mcp-config.json — add inside mcpServers"
            headerAction={
              <button
                type="button"
                onClick={() => void copyConfig()}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-all",
                  copiedConfig
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "bg-white/[0.06] text-muted-foreground hover:bg-white/[0.1] hover:text-foreground"
                )}
              >
                <ClipboardCopy className="h-3 w-3" />
                {copiedConfig ? "Copied!" : "Copy"}
              </button>
            }
          >
            <div className="space-y-1">
              <p><span className="text-blue-400">{'"figma"'}</span>: {"{"}</p>
              <p>{"  "}<span className="text-blue-400">{'"type"'}</span>: <span className="text-emerald-400">{'"stdio"'}</span>,</p>
              <p>{"  "}<span className="text-blue-400">{'"command"'}</span>: <span className="text-emerald-400">{'"npx"'}</span>,</p>
              <p>{"  "}<span className="text-blue-400">{'"args"'}</span>: [<span className="text-emerald-400">{`"-y"`}</span>, <span className="text-emerald-400">{`"@anthropic/figma-mcp-server"`}</span>],</p>
              <p>{"  "}<span className="text-blue-400">{'"env"'}</span>: {"{"}</p>
              <p>
                {"    "}
                <span className="text-blue-400">{'"FIGMA_PERSONAL_ACCESS_TOKEN"'}</span>: {" "}
                <span className={cn(hasValidFigmaToken ? "text-emerald-400" : "text-amber-400")}>
                  {JSON.stringify(trimmedFigmaToken || FIGMA_TOKEN_PLACEHOLDER)}
                </span>
              </p>
              <p>{"  "}{"}"}</p>
              <p>{"}"}</p>
            </div>
          </CopyableTerminal>
        ) : (
          <CopyableTerminal title="Terminal" copyText={figmaMcpAddCommand}>
            <p className="whitespace-pre-wrap break-all">
              <span className="text-foreground/45">$ </span>
              <span className="text-foreground">{figmaMcpAddCommand}</span>
            </p>
          </CopyableTerminal>
        )}
        <p className="mt-2 text-sm leading-7 text-muted-foreground">
          Get your Figma token from{" "}
          <strong className="text-foreground/90">Figma {">"} Settings {">"} Personal Access Tokens {">"} Generate</strong>.
        </p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground/60">
          The <code className="rounded bg-white/[0.06] px-1 py-0.5 font-mono text-[10px]">@anthropic/figma-mcp-server</code>{" "}
          package is the official Figma MCP server. MCP is an open protocol — this server works
          with any MCP-compatible client including {getHostLabel(host)}.
        </p>
      </NumberedInstruction>

      <DesignerPlatformToolSetup platform={designerPlatform} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 3 — Use Designer                                                     */
/* -------------------------------------------------------------------------- */

const DESIGNER_EXAMPLE_URL = "https://figma.com/design/AbC123/My-Project?node-id=42-1337";

const DESIGNER_INVOCATION_EXAMPLES: Record<DesignerPlatform, { build: string; audit: string }> = {
  web: {
    build: "Build this Figma frame",
    audit: "Run a UX audit on this Figma frame",
  },
  ios: {
    build: "Build this iOS screen from this Figma frame for the currently running Simulator",
    audit: "Run a UX audit on this iOS screen from this Figma frame using the currently running Simulator",
  },
  android: {
    build: "Build this Android screen from this Figma frame for the currently running emulator",
    audit: "Run a UX audit on this Android screen from this Figma frame using the currently running emulator",
  },
};

const DESIGNER_PLATFORM_NOTES: Partial<Record<DesignerPlatform, string>> = {
  ios: "For iOS targets, make sure your Simulator is running before invoking.",
  android: "For Android targets, make sure your emulator is running before invoking.",
};

function DesignerInvocationExample({
  title,
  instruction,
}: {
  title: string;
  instruction: string;
}) {
  const command = `Invoke SpecTree-Designer. ${instruction}:`;

  return (
    <CopyableTerminal title={title} copyText={`${command}\n${DESIGNER_EXAMPLE_URL}`}>
      <div className="space-y-1.5">
        <p>
          <span className="text-amber-400/70">{">"} </span>
          <span className="text-foreground">{command}</span>
        </p>
        <p className="text-muted-foreground/70">
          {"  "}{DESIGNER_EXAMPLE_URL}
        </p>
      </div>
    </CopyableTerminal>
  );
}

export function UseDesignerStep() {
  const host = useResolvedHost();
  const { selectedDesignerPlatform } = useOnboarding();
  const designerPlatform = selectedDesignerPlatform ?? "web";
  const examples = DESIGNER_INVOCATION_EXAMPLES[designerPlatform];
  const platformNote = DESIGNER_PLATFORM_NOTES[designerPlatform];

  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Get a Figma frame link">
        <p className="text-sm leading-7 text-muted-foreground">
          Open your Figma file, select the frame or component you want to implement, then
          right-click and choose <strong className="text-foreground/90">Copy link to selection</strong>.
          The URL should look like:
        </p>
        <CopyableTerminal title="Example Figma URL" copyText={DESIGNER_EXAMPLE_URL}>
          <p className="break-all text-xs text-muted-foreground/70">
            {DESIGNER_EXAMPLE_URL}
          </p>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Invoke the Designer skill">
        <p className="text-sm leading-7 text-muted-foreground">
          In your {getHostLabel(host)} session, invoke the Designer skill with your Figma URL:
        </p>
        <div className="space-y-3">
          <DesignerInvocationExample title="Build mode example" instruction={examples.build} />
          <DesignerInvocationExample title="UX Audit mode example" instruction={examples.audit} />
        </div>
        {platformNote ? (
          <p className="text-sm leading-7 text-muted-foreground">{platformNote}</p>
        ) : null}
        <p className="text-sm leading-7 text-muted-foreground">
          Designer auto-detects your platform, framework, styling approach, and component
          library. It will reuse your existing tokens and shared components wherever
          possible.
        </p>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Review the results">
        <p className="text-sm leading-7 text-muted-foreground">
          Build and Fix runs show a side-by-side comparison — the Figma reference, your
          implementation, and a diff overlay highlighting remaining differences. UX Audit
          runs generate an accessibility and design-system compliance report instead of a
          visual diff.
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          <InfoCard
            icon={Layers}
            title="Quadrant analysis"
            description="Build and Fix diffs are broken into four quadrants so you can see exactly where differences are concentrated."
            accentClass="text-blue-400"
          />
          <InfoCard
            icon={Settings}
            title="Auto-iteration"
            description="Up to 5 rounds of fixes — layout first, then colors, typography, spacing, and effects, in priority order."
            accentClass="text-blue-400"
          />
          <InfoCard
            icon={CheckCircle2}
            title="Final report"
            description="Build/Fix runs end with fidelity history and reuse stats, while UX Audit highlights accessibility issues, design-system drift, and recommended next steps."
            accentClass="text-blue-400"
          />
        </div>
      </NumberedInstructionLast>
    </div>
  );
}
